import React, { useState, useMemo, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Search, X, Check } from "lucide-react";

/**
 * Single-student picker con ricerca type-ahead.
 * Props: students[], value (student_id | ""), onChange(id), testId, placeholder
 */
export function StudentPicker({ students = [], value, onChange, testId = "student-picker", placeholder = "Cerca studente…" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  const selected = useMemo(() => students.find((s) => s.id === value), [students, value]);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return students.slice(0, 40);
    return students.filter((s) => {
      const full = `${s.first_name || ""} ${s.last_name || ""} ${s.matricola || ""} ${s.email || ""}`.toLowerCase();
      return full.includes(term);
    }).slice(0, 40);
  }, [students, q]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={wrapRef} className="relative mt-1.5">
      {selected && !open ? (
        <div className="flex items-center justify-between gap-2 px-3 py-2 border border-stone-200 rounded-md text-sm bg-emerald-50/40" data-testid={`${testId}-chip`}>
          <span className="truncate">
            <b>{selected.first_name} {selected.last_name}</b>
            {selected.matricola && <span className="text-stone-500"> · {selected.matricola}</span>}
          </span>
          <button type="button" onClick={() => { onChange(""); setQ(""); setOpen(true); }} className="text-stone-400 hover:text-red-600" data-testid={`${testId}-clear`}>
            <X size={14}/>
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input
              data-testid={testId}
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              className="pl-9"
              autoComplete="off"
            />
          </div>
          {open && filtered.length > 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 border border-stone-200 rounded-md bg-white shadow-lg max-h-56 overflow-y-auto">
              {filtered.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`${testId}-opt-${s.id}`}
                  onClick={() => { onChange(s.id); setOpen(false); setQ(""); }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-stone-50 flex items-center gap-2"
                >
                  <span className="flex-1 truncate">{s.first_name} {s.last_name}</span>
                  <span className="text-[10px] text-stone-500">{s.year_level ? `${s.year_level}° ${s.track_type || ""}` : ""}</span>
                </button>
              ))}
            </div>
          )}
          {open && filtered.length === 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 border border-stone-200 rounded-md bg-white shadow-lg px-3 py-3 text-xs text-stone-400">
              Nessuno studente trovato
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Multi-student picker con ricerca type-ahead e chip removibili.
 */
export function StudentPickerMulti({ students = [], values = [], onChange, testId = "students-picker", placeholder = "Cerca e aggiungi studente…" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selectedSet = new Set(values);
  const selectedList = useMemo(() => students.filter((s) => selectedSet.has(s.id)), [students, values]);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const base = students.filter((s) => !selectedSet.has(s.id));
    if (!term) return base.slice(0, 40);
    return base.filter((s) => {
      const full = `${s.first_name || ""} ${s.last_name || ""} ${s.matricola || ""} ${s.email || ""}`.toLowerCase();
      return full.includes(term);
    }).slice(0, 40);
  }, [students, q, values]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const add = (id) => { onChange([...values, id]); setQ(""); };
  const remove = (id) => onChange(values.filter((v) => v !== id));

  return (
    <div ref={wrapRef} className="relative mt-1.5">
      <div className="border border-stone-200 rounded-md p-2 min-h-[42px] bg-white flex flex-wrap gap-1.5">
        {selectedList.map((s) => (
          <span key={s.id} data-testid={`${testId}-chip-${s.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs">
            {s.first_name} {s.last_name}
            <button type="button" onClick={() => remove(s.id)} className="text-emerald-800 hover:text-red-600">
              <X size={11}/>
            </button>
          </span>
        ))}
        <div className="flex-1 relative min-w-[140px]">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400"/>
          <input
            data-testid={testId}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={selectedList.length === 0 ? placeholder : ""}
            className="w-full pl-6 pr-1 py-0.5 text-sm outline-none bg-transparent"
            autoComplete="off"
          />
        </div>
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 border border-stone-200 rounded-md bg-white shadow-lg max-h-56 overflow-y-auto">
          {filtered.map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={`${testId}-opt-${s.id}`}
              onClick={() => add(s.id)}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-stone-50 flex items-center gap-2"
            >
              <Check size={12} className="text-emerald-600 opacity-0"/>
              <span className="flex-1 truncate">{s.first_name} {s.last_name}</span>
              <span className="text-[10px] text-stone-500">{s.year_level ? `${s.year_level}° ${s.track_type || ""}` : ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default StudentPicker;
